import { useCallback, useEffect, useRef, useState } from "react";
import { previewCode } from "./api";

/**
 * The preview of one promo code or gift card against a plan (contract §5.1).
 *
 * EXPLICIT, NOT DEBOUNCED. The preview endpoint is unauthenticated and rate-limited
 * (20/min/client) and a partially typed code is always invalid, so a request per
 * keystroke would burn the budget on noise and flash "invalid" at a user who has not
 * finished typing. The request goes out when the user presses Apply, and only then.
 *
 *   status  "idle" | "loading" | "applied" | "error"
 *   preview the §5.1 payload of the last successful apply, else null
 *   error   the axios error of the last failed apply, else null
 *   apply(code, {plan_id, currency, billing_cycle}) -> Promise<preview | null>
 *   clear() back to idle
 *
 * A response that arrives after a newer apply (or a clear, or an unmount) is dropped:
 * a slow answer for the previous code must never overwrite the current one.
 */
export default function useSaasCodePreview() {
  const [state, setState] = useState({ status: "idle", preview: null, error: null });
  const seq = useRef(0);

  useEffect(() => () => { seq.current += 1; }, []);

  const apply = useCallback(async (code, { plan_id, currency, billing_cycle }) => {
    const mine = ++seq.current;
    setState({ status: "loading", preview: null, error: null });
    try {
      const preview = await previewCode({ code, plan_id, currency, billing_cycle });
      if (mine !== seq.current) return null;
      setState({ status: "applied", preview, error: null });
      return preview;
    } catch (error) {
      if (mine !== seq.current) return null;
      setState({ status: "error", preview: null, error });
      return null;
    }
  }, []);

  const clear = useCallback(() => {
    seq.current += 1;
    setState({ status: "idle", preview: null, error: null });
  }, []);

  return { ...state, apply, clear };
}

/**
 * The preview params for a practice that already exists (Billing page): its operating
 * currency and cycle, priced against `planId` (the current plan, or a plan-change target).
 * A practice with no stored cycle is billed monthly — the server's own reading of
 * `tenants.billing_cycle` (tenant_self_service.py) — so the preview prices it the same way.
 */
export const tenantPricing = (tenant, planId = tenant.plan_id) => ({
  plan_id: planId,
  currency: tenant.currency,
  billing_cycle: tenant.billing_cycle === "annual" ? "annual" : "monthly",
});
